import { computed, getCurrentScope, onScopeDispose, reactive, ref, type CSSProperties } from 'vue';

/**
 * Map of `columnId → width in px`. The composable mutates this object in place
 * during drag, so callers should bind it as a reactive source (it already is —
 * the composable returns a `reactive`-wrapped object).
 */
export type ColumnWidths = Record<string, number>;

export interface UseResizableColumnsOptions {
  /**
   * Stable key used to persist widths in `localStorage`. Versioned by callers
   * (e.g. `audio-file-issues:columnWidths:v1`) so that schema changes to the
   * column set can invalidate stored values.
   */
  storageKey: string;
  /** Starting widths used before localStorage values are applied. */
  defaults: ColumnWidths;
  /** Lower bound enforced during drag and on hydration. Default 80px. */
  minWidth?: number;
  /** Upper bound enforced during drag and on hydration. Default 800px. */
  maxWidth?: number;
}

const DEFAULT_MIN_WIDTH_PX = 80;
const DEFAULT_MAX_WIDTH_PX = 800;
const KEYBOARD_NUDGE_PX = 8;
const KEYBOARD_NUDGE_LARGE_PX = 32;

/**
 * Pointerdown/move/up state machine that drives per-column width resizing
 * for a `table-fixed` table. The composable is intentionally generic — it
 * knows nothing about which table it's serving, so callers pass a
 * `storageKey` plus a starting `defaults` map.
 *
 * The returned `widths` object is `reactive`, so bindings like
 * `:style="getColumnStyle(id)"` update synchronously during drag and the
 * browser only needs to repaint, not relayout, the rest of the page.
 */
export function useResizableColumns(options: UseResizableColumnsOptions) {
  const minWidth = options.minWidth ?? DEFAULT_MIN_WIDTH_PX;
  const maxWidth = options.maxWidth ?? DEFAULT_MAX_WIDTH_PX;

  function clamp(width: number): number {
    return Math.min(maxWidth, Math.max(minWidth, Math.round(width)));
  }

  function readStoredWidths(): ColumnWidths {
    if (typeof window === 'undefined') {
      return {};
    }
    try {
      const rawValue = window.localStorage.getItem(options.storageKey);
      if (!rawValue) {
        return {};
      }
      const parsed: unknown = JSON.parse(rawValue);
      if (!parsed || typeof parsed !== 'object') {
        return {};
      }
      const sanitized: ColumnWidths = {};
      for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
        if (typeof value === 'number' && Number.isFinite(value)) {
          sanitized[key] = clamp(value);
        }
      }
      return sanitized;
    } catch {
      return {};
    }
  }

  const widths = reactive<ColumnWidths>({ ...options.defaults, ...readStoredWidths() });

  // Mutable drag state lives outside reactivity — only `widths` and
  // `activeColumnId` need to trigger renders.
  const activeColumnId = ref<string | null>(null);
  let pointerStartX = 0;
  let widthAtPointerDown = 0;
  let activeHandleEl: HTMLElement | null = null;
  let activePointerId: number | null = null;
  let didMoveDuringDrag = false;
  let previousBodyUserSelect = '';
  let previousBodyCursor = '';

  function persist() {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(options.storageKey, JSON.stringify(widths));
    } catch {
      // Quota or private-mode failures shouldn't break interactive resize.
    }
  }

  function lockBodyForDrag() {
    if (typeof document === 'undefined') {
      return;
    }
    previousBodyUserSelect = document.body.style.userSelect;
    previousBodyCursor = document.body.style.cursor;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';
  }

  function unlockBodyAfterDrag() {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.style.userSelect = previousBodyUserSelect;
    document.body.style.cursor = previousBodyCursor;
  }

  function handlePointerMove(event: PointerEvent) {
    if (!activeColumnId.value) {
      return;
    }
    didMoveDuringDrag = true;
    const deltaX = event.clientX - pointerStartX;
    widths[activeColumnId.value] = clamp(widthAtPointerDown + deltaX);
  }

  function finishResize(event: PointerEvent) {
    if (!activeColumnId.value) {
      return;
    }
    if (activeHandleEl) {
      activeHandleEl.removeEventListener('pointermove', handlePointerMove);
      activeHandleEl.removeEventListener('pointerup', finishResize);
      activeHandleEl.removeEventListener('pointercancel', finishResize);
      if (activePointerId !== null) {
        try {
          activeHandleEl.releasePointerCapture(event.pointerId);
        } catch {
          // Releasing capture can throw if the pointer was already lifted.
        }
      }
    }
    activeHandleEl = null;
    activePointerId = null;
    activeColumnId.value = null;
    unlockBodyAfterDrag();
    // Zero-move clicks never touched `widths`, so there is nothing to commit —
    // persisting would needlessly freeze auto-sized columns at their defaults.
    if (didMoveDuringDrag) {
      persist();
    }
    didMoveDuringDrag = false;
  }

  /**
   * Begin a resize drag for `columnId`. Bind to `pointerdown` on the resize
   * handle (`@pointerdown="startResize('code', $event)"`). The handle becomes
   * the pointer-capture target so the drag survives quick moves outside the
   * table cell. Callers may pass `startWidth` (e.g. the measured header width
   * for an auto-sized column) — it seeds the drag without mutating `widths`,
   * so the column only converts to a fixed width on real pointer movement.
   */
  function startResize(columnId: string, event: PointerEvent, startWidth?: number) {
    // Only left mouse button / primary pointer should start a resize.
    if (event.button !== 0 && event.pointerType === 'mouse') {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    const handleEl = event.currentTarget as HTMLElement | null;
    if (!handleEl) {
      return;
    }
    activeColumnId.value = columnId;
    pointerStartX = event.clientX;
    widthAtPointerDown = startWidth ?? widths[columnId] ?? options.defaults[columnId] ?? minWidth;
    didMoveDuringDrag = false;
    activeHandleEl = handleEl;
    activePointerId = event.pointerId;
    lockBodyForDrag();
    try {
      handleEl.setPointerCapture(event.pointerId);
    } catch {
      // Pointer capture is best-effort; jsdom and some browsers refuse.
    }
    handleEl.addEventListener('pointermove', handlePointerMove);
    handleEl.addEventListener('pointerup', finishResize);
    handleEl.addEventListener('pointercancel', finishResize);
  }

  /**
   * Optional ←/→ keyboard nudging for accessibility. Bind to `keydown` on the
   * focusable resize handle. Shift+arrow nudges by a larger step.
   */
  function onHandleKeydown(columnId: string, event: KeyboardEvent) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }
    event.preventDefault();
    const stepPx = event.shiftKey ? KEYBOARD_NUDGE_LARGE_PX : KEYBOARD_NUDGE_PX;
    const direction = event.key === 'ArrowRight' ? 1 : -1;
    const currentWidth = widths[columnId] ?? options.defaults[columnId] ?? minWidth;
    widths[columnId] = clamp(currentWidth + direction * stepPx);
    persist();
  }

  /** Restore every column to its starting `defaults` width and persist. */
  function resetWidths() {
    for (const key of Object.keys(widths)) {
      delete widths[key];
    }
    Object.assign(widths, options.defaults);
    persist();
  }

  /**
   * Inline `style` for `<th>` / `<col>` bindings. Returns `undefined` for
   * unknown column ids so callers can opt out per-column without seeding a
   * default.
   */
  function getColumnStyle(columnId: string): CSSProperties | undefined {
    const widthPx = widths[columnId];
    if (typeof widthPx !== 'number') {
      return undefined;
    }
    const widthValue = `${widthPx}px`;
    return {
      width: widthValue,
      minWidth: widthValue,
      maxWidth: widthValue,
    };
  }

  // If the host component unmounts mid-drag, detach the handle listeners and
  // restore the body lock — otherwise user-select/cursor stay broken app-wide.
  // Guarded so the composable stays callable outside a component scope.
  if (getCurrentScope()) {
    onScopeDispose(() => {
      if (!activeColumnId.value) {
        return;
      }
      if (activeHandleEl) {
        activeHandleEl.removeEventListener('pointermove', handlePointerMove);
        activeHandleEl.removeEventListener('pointerup', finishResize);
        activeHandleEl.removeEventListener('pointercancel', finishResize);
      }
      activeHandleEl = null;
      activePointerId = null;
      activeColumnId.value = null;
      unlockBodyAfterDrag();
    });
  }

  const isResizing = computed(() => activeColumnId.value !== null);

  return {
    widths,
    isResizing,
    activeColumnId,
    minWidth,
    maxWidth,
    startResize,
    onHandleKeydown,
    resetWidths,
    getColumnStyle,
  };
}
